'use client';

import { useState } from 'react';

interface TempPasswordModalProps {
  data: {
    user_name: string;
    email: string;
    temp_password: string;
    message: string;
    action: 'provisioned' | 'reset';
  } | null;
  onClose: () => void;
}

const TempPasswordModal = ({ data, onClose }: TempPasswordModalProps) => {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const handleCopyAll = async () => {
    const block = `NextRoof 360 Login Credentials\n\nEmail: ${data.email}\nTemporary Password: ${data.temp_password}\n\nPlease change your password after first login.`;
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback — user can manually copy
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(data.temp_password);
    } catch {
      // fallback
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1C2E5E]">
                {data.action === 'provisioned' ? 'Login Created' : 'Password Reset'}
              </h3>
              <p className="text-[12px] text-gray-600 mt-0.5">{data.user_name}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-[11px] font-bold text-amber-900">⚠ This is the only time this password will be shown</p>
            <p className="text-[11px] text-amber-800 mt-1">
              Copy it now and share it securely with the user. The user should change it after first login.
            </p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Email</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 text-[12px] font-mono bg-gray-50 border border-gray-200 rounded-lg text-gray-800 break-all">
                {data.email}
              </code>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Temporary Password</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 text-[14px] font-bold font-mono bg-blue-50 border border-blue-200 rounded-lg text-[#1C2E5E] tracking-wide select-all">
                {data.temp_password}
              </code>
              <button
                type="button"
                onClick={handleCopyPassword}
                className="px-3 py-2 rounded-lg text-[11px] font-bold text-white bg-[#E89A3C] hover:bg-[#0891c9]"
                title="Copy password only"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCopyAll}
              className="w-full px-4 py-2.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
            >
              {copied ? '✓ Copied All Credentials' : '📋 Copy Email + Password to Share'}
            </button>
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              Paste into Slack/email/text to securely deliver to {data.user_name.split(' ')[0]}
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TempPasswordModal;
